/**
 * One finding, in full.
 *
 * The reading column carries the argument — why it matters, how to fix it,
 * which URLs are affected — while the evidence and timeline stay pinned beside
 * it, because the evidence is what you check *against* the explanation rather
 * than after it.
 */

import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { issueApi } from '@/api/endpoints';
import { useAuth } from '@/app/auth';
import { Permission } from '@/app/permissions';
import {
  AlertIcon,
  ArrowRightIcon,
  ClockIcon,
  DocumentIcon,
  InfoIcon,
  ShieldIcon,
} from '@/components/icons';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  DataRow,
  DetailLayout,
  DetailMain,
  DetailRail,
  ErrorState,
  LoadingState,
  MetaBar,
  PageHeader,
  SeverityBadge,
  Table,
  Td,
  Th,
} from '@/components/ui';
import { formatDate } from '@/utils/format';

export function IssueDetailPage() {
  const { siteId = '', issueId = '' } = useParams();
  const { can } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({ queryKey: ['issue', issueId], queryFn: () => issueApi.get(issueId) });

  const updateMutation = useMutation({
    mutationFn: (status: 'acknowledged' | 'ignored' | 'open') =>
      issueApi.update(issueId, { status }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['issue', issueId] });
      await queryClient.invalidateQueries({ queryKey: ['issues', siteId] });
    },
  });

  if (query.isLoading) return <LoadingState />;
  if (query.isError) {
    return (
      <ErrorState message={(query.error as Error).message} onRetry={() => void query.refetch()} />
    );
  }

  const issue = query.data!;
  const evidence = Object.entries(issue.evidence);

  return (
    <>
      <PageHeader
        breadcrumb={
          <Link
            to={`/sites/${siteId}/issues`}
            className="inline-flex items-center gap-1 hover:text-ink"
          >
            <ArrowRightIcon className="h-3.5 w-3.5 rotate-180" />
            All issues
          </Link>
        }
        title={issue.title}
        description={issue.description}
        actions={
          can(Permission.ISSUE_UPDATE) ? (
            <>
              {issue.status === 'open' ? (
                <Button
                  variant="secondary"
                  onClick={() => updateMutation.mutate('acknowledged')}
                  loading={updateMutation.isPending}
                >
                  Acknowledge
                </Button>
              ) : null}
              {issue.status !== 'ignored' ? (
                <Button
                  onClick={() => updateMutation.mutate('ignored')}
                  loading={updateMutation.isPending}
                >
                  Ignore
                </Button>
              ) : (
                <Button
                  onClick={() => updateMutation.mutate('open')}
                  loading={updateMutation.isPending}
                >
                  Reopen
                </Button>
              )}
            </>
          ) : null
        }
      />

      <MetaBar>
        <SeverityBadge severity={issue.severity} />
        <Badge tone="neutral">{issue.category.replace(/_/g, ' ')}</Badge>
        <Badge tone={issue.status === 'resolved' ? 'success' : 'neutral'}>{issue.status}</Badge>
        <Badge tone="neutral">{issue.affected_url_count} affected URL(s)</Badge>
        <code className="rounded bg-surface-muted px-1.5 py-0.5 font-mono text-[11px] text-ink-subtle">
          {issue.check_id}
        </code>
      </MetaBar>

      <DetailLayout>
        <DetailMain>
          {issue.why_it_matters ? (
            <Card>
              <CardHeader title="Why this matters" icon={<AlertIcon className="h-4 w-4" />} />
              <p className="text-sm leading-relaxed text-ink-muted">{issue.why_it_matters}</p>
            </Card>
          ) : null}

          {issue.how_to_fix ? (
            <Card>
              <CardHeader title="How to fix it" icon={<ShieldIcon className="h-4 w-4" />} />
              <p className="text-sm leading-relaxed text-ink-muted">{issue.how_to_fix}</p>
              {issue.documentation_url ? (
                <a
                  href={issue.documentation_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-brand hover:underline"
                >
                  Reference documentation
                  <ArrowRightIcon className="h-3.5 w-3.5" />
                </a>
              ) : null}
            </Card>
          ) : null}

          {issue.affected_urls.length > 0 ? (
            <Card>
              <CardHeader
                title="Affected URLs"
                icon={<DocumentIcon className="h-4 w-4" />}
                description={
                  issue.affected_url_count > issue.affected_urls.length
                    ? `Showing ${issue.affected_urls.length} of ${issue.affected_url_count}.`
                    : undefined
                }
              />
              <Table>
                <thead>
                  <tr>
                    <Th>URL</Th>
                    <Th>Detail</Th>
                  </tr>
                </thead>
                <tbody>
                  {issue.affected_urls.slice(0, 100).map((entry, index) => (
                    <tr key={`${entry.url}-${index}`}>
                      <Td>
                        <a
                          href={entry.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-mono text-xs break-all text-brand hover:underline"
                        >
                          {entry.url}
                        </a>
                      </Td>
                      <Td className="text-xs text-ink-muted">{entry.detail ?? '—'}</Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card>
          ) : null}
        </DetailMain>

        <DetailRail>
          <Card>
            <CardHeader
              title="Evidence"
              icon={<InfoIcon className="h-4 w-4" />}
              description="The observations behind this finding."
            />
            {evidence.length === 0 ? (
              <p className="text-sm text-ink-muted">No structured evidence was recorded.</p>
            ) : (
              <div className="divide-y divide-border-subtle">
                {evidence.map(([key, value]) => (
                  <DataRow key={key} label={<span title={key}>{key.replace(/_/g, ' ')}</span>}>
                    <span className="tnum truncate text-ink" title={renderValue(value)}>
                      {renderValue(value)}
                    </span>
                  </DataRow>
                ))}
              </div>
            )}
          </Card>

          <Card>
            <CardHeader title="Timeline" icon={<ClockIcon className="h-4 w-4" />} />
            <div className="divide-y divide-border-subtle">
              <DataRow label="First detected">
                <span className="text-ink">{formatDate(issue.first_detected_at)}</span>
              </DataRow>
              {issue.resolved_at ? (
                <DataRow label="Resolved">
                  <span className="text-success">{formatDate(issue.resolved_at)}</span>
                </DataRow>
              ) : null}
            </div>
            {issue.ignore_reason ? (
              <div className="mt-3 rounded-lg bg-surface-muted p-3">
                <p className="text-xs font-medium text-ink-subtle">Ignore reason</p>
                <p className="mt-1 text-sm text-ink-muted">{issue.ignore_reason}</p>
              </div>
            ) : null}
          </Card>
        </DetailRail>
      </DetailLayout>
    </>
  );
}

function renderValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'number') return value.toLocaleString();
  if (typeof value === 'boolean') return value ? 'yes' : 'no';
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return `${value.length} item(s)`;
  return JSON.stringify(value).slice(0, 200);
}
